import { Fragment, h } from 'preact';
import { useContext, useEffect } from 'preact/compat';
import { Theme } from '../lib/render';
import { CartActionType, useCart, formatCurrency } from '../hooks/cart';
import { useDonationStep, DonationStepEnum } from '../hooks/step';
import Image from './Image';
import Arrow from '../icons/Arrow';
import purifyHTML, { slugify } from '../lib/purify';
import SocialShare from './SocialShare';
import { scrollToSelector } from '../lib/helpers';

/**
 * Success.
 * Generally rendered outside of standard flow container.
 * Social shares are absolutely positioned to viewport.
 *
 * @param props.embedded Whether the component is within non-full-page content.
 * Generally used for individual pages.
 */
const Success = ({
	embedded = false,
}:{
	embedded?: boolean,
}) => {
	const theme = useContext(Theme);
	const cart = useCart();
	const step = useDonationStep();

	const {
		title,
		message,
		featured_message,
		featured_title,
		content,
	} = theme.success;

	// Total swap.
	const regex = /%total%/gm;

	// Scroll to top on mount.
	useEffect(() => {
		scrollToSelector('.ea-success', -50, 200);

		// Attach class to body to nullify external footer margin.
		document.body.classList.add('ea-success-body-active');
		return () => {
			document.body.classList.remove('ea-success-body-active');
		};
	}, []);

	return (
		<div
			className={[
				'ea-success',
				embedded ? 'ea-success--embedded' : 'ea-success--full-width',
			].join(' ')}
		>
			<div
				className={[
					'ea-success__inner',
					embedded ? '' : 'container',
				].join(' ')}
			>
				{/**
				 * No return btn on individual pages.
				 */
					!embedded && (
						<div className="ea-success__return">
							<button
								type="button"
								className="ea-btn-reset ea-btn-reset--linklike"
								onClick={() => {
								// Return to entry.
									step.dispatch({
										type: 'MANUAL_STEP',
										payload: {
											step: DonationStepEnum.ENTRY,
										},
									});
									// Reset cart.
									cart.dispatch({
										type: CartActionType.CLEAR_SELECTIONS,
									});
								}}
							>
								Back to
								{` ${theme.donate_type_label}`}
							</button>
						</div>
					)
				}
				<div className="ea-success__content">
					{/**
					 * Ensure accessible order depending on context.
					 */
						title && (
							embedded ? (
								<h2 className="h3 ea-success__title">
									{title}
								</h2>
							) : (
								<h1 className="ea-success__title">
									{title}
								</h1>
							)
						)
					}
					{message && (
						<p
							className="ea-success__message"
							// Swap %total% for the total.
							dangerouslySetInnerHTML={{
								__html: purifyHTML(message.replace(regex, `<strong>$${formatCurrency(cart.cart.total)}</strong>`)),
							}}
						/>
					)}
				</div>
				<SocialShare />
				<div className="ea-success-featured">
					{featured_title && (
						<h2
							className={[
								'ea-success-featured__title',
								embedded ? 'h5' : 'h4',
							].join(' ')}
						>
							{featured_title}
						</h2>
					)}
					{featured_message && (<p className="ea-success-featured__desc">{featured_message}</p>)}
					<div className="ea-success-featured-items">
						{content && content.length > 0 && (
							<Fragment>
								{content.map((item, i) => (
									<div key={`${slugify(item.title)}}`} className="ea-success-f-item">
										<div className="ea-success-f-item__image">
											<Image
												{...item.image}
												width={400}
												height={225}
											/>
										</div>
										<div className="ea-success-f-item__content">
											{item.title && (
												<h3 className="h6 ea-success-f-item__title">{item.title}</h3>
											)}
											{item.link && item.link.url && item.link.title && (
												<a
													href={item.link.url}
													className="ea-arrow-link"
													target={item.link.target
														? item.link.target : '_self'}
													rel={item.link.target === '_blank' ? 'noopener noreferrer' : ''}
												>
													<span>{item.link.title}</span>
													<Arrow />
												</a>
											)}
										</div>
									</div>
								))}
							</Fragment>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

export default Success;
